import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SHADING_STATUS } from 'constants/index';
import { changePanel, fetchRoofData } from '../RoofDataSlice/roofDataSlice';
import { TransformedSolarData } from '../RoofDataSlice/types';
import {
	EditDsmResponse,
	FacetEditShadingStatus,
	RecalcResponse,
	ShadingStatusSnap,
} from 'api/editDsm';

export interface DsmShadingState {
  dsmVersion: number; // bumped by every edit response — DSM or facet
  shadingVersion: number; // which dsmVersion the shown production reflects
  shadingStatus: ShadingAnalysis['shadingStatus'] | null;
  inSync: boolean; // dsmVersion === shadingVersion
  dsmDirty: boolean; // unbaked DSM edits → the next run is the full bake
  runType: ShadingRunType; // which script the outstanding/in-flight run is
  // transient UI (blacklisted from persist — reconciled from the status poll on load):
  isRecomputing: boolean; // "Done" clicked, waiting for completed + inSync
  shadingError: string | null;
}

const initialState: { data: DsmShadingState } = {
	data: {
		dsmVersion: 0,
		shadingVersion: 0,
		shadingStatus: null,
		inSync: true,
		dsmDirty: false,
		runType: 'facet-edit',
		isRecomputing: false,
		shadingError: null,
	},
};

const dsmShadingSlice = createSlice({
	name: 'dsmShading',
	initialState,
	reducers: {
		// DSM edit saved (per edit, or the batched flush on Done): bump version, mark dirty.
		// No recompute.
		applyDsmEditResult(state, { payload }: PayloadAction<EditDsmResponse>) {
			const s = state.data;
			s.dsmVersion = Math.max(s.dsmVersion, payload.dsmVersion);
			s.shadingStatus = payload.shadingStatus;
			s.inSync = payload.inSync; // false → "unsaved changes"
			s.dsmDirty = payload.dsmDirty; // always true here — a DSM edit is what dirties it
			s.runType = payload.runType;
			s.shadingError = null;
		},

		// Facet edit saved (batch of settled create/translate mutations): same deal, but the
		// DSM stays as clean as it was — a facet edit never escalates the run on its own.
		setFacetShadingState(state, { payload }: PayloadAction<FacetEditShadingStatus>) {
			const s = state.data;
			// Math.max: this lands from a batch of settled mutations, so a late straggler must
			// not drag the version below the edit a watch is baselined on.
			s.dsmVersion = Math.max(s.dsmVersion, payload.dsmVersion);
			s.shadingStatus = payload.shadingStatus;
			s.inSync = payload.inSync; // false → "unsaved changes"
			s.runType = payload.runType;
			s.shadingError = null;
		},

		// recalculate-shading accepted (202): enter "recomputing…". One action for both edit
		// kinds now — `runType` says which script the server picked.
		shadingRecalcAccepted(state, { payload }: PayloadAction<RecalcResponse>) {
			const s = state.data;
			// Don't claim a run that wasn't enqueued: watching one would poll a version
			// nothing is baking until the timeout fires.
			if (!payload.accepted) {
				s.isRecomputing = false;
				return;
			}
			s.dsmVersion = Math.max(s.dsmVersion, payload.dsmVersion);
			// The real current status, not always QUEUED: firing this while a run is already going
			// returns RUNNING (the duplicate is dropped queue-side), so take it as a free reading.
			s.shadingStatus = payload.shadingStatus;
			s.dsmDirty = payload.dsmDirty;
			s.runType = payload.runType;
			s.isRecomputing = true;
			s.shadingError = null;
		},

		// single reconciler for status polls. Never moves versions backward.
		syncShadingState(state, { payload }: PayloadAction<ShadingStatusSnap>) {
			const s = state.data;
			s.dsmVersion = Math.max(s.dsmVersion, payload.dsmVersion);
			s.shadingVersion = Math.max(s.shadingVersion, payload.shadingVersion);
			s.shadingStatus = payload.shadingStatus;
			s.inSync = payload.inSync;
			s.dsmDirty = payload.dsmDirty;
			s.runType = payload.runType;
			// "recomputing" means a run is actually live. Every other status the poll can report ends
			// the watch — COMPLETED (done, or dirty again), FAILED, or PENDING (nobody asked for a run)
			// — so the flag has to clear here, otherwise the spinner outlives the poll loop.
			s.isRecomputing = payload.shadingStatus === SHADING_STATUS.QUEUED || payload.shadingStatus === SHADING_STATUS.RUNNING;
			if (payload.shadingStatus === SHADING_STATUS.COMPLETED && payload.inSync) s.shadingError = null;
			if (payload.shadingStatus === SHADING_STATUS.FAILED)
				s.isRecomputing = false;
		},

		syncCreateShadingState(state, { payload }: PayloadAction<ShadingStatusSnap>) {
			const s = state.data;
			s.dsmVersion = Math.max(s.dsmVersion, payload.dsmVersion);
			s.shadingVersion = Math.max(s.shadingVersion, payload.shadingVersion);
			s.shadingStatus = payload.shadingStatus;
			s.inSync = payload.inSync;
			s.dsmDirty = payload.dsmDirty;
			s.runType = payload.runType;
		},

		shadingFailed(state, { payload }: PayloadAction<{ message?: string }>) {
			const s = state.data;
			s.shadingStatus = SHADING_STATUS.FAILED;
			s.isRecomputing = false;
			s.shadingError = payload.message ?? 'Shading recompute failed';
		},

		clearShadingError(state) {
			state.data.shadingError = null;
		},

		/**
		 * Optimistic: the user clicked "Done" — reflect "recomputing" immediately, before the
		 * edit-flush + recalculate POSTs resolve.
		 *
		 * Also the unambiguous cue for dsmShadingListener to tear down any watch still polling
		 * the PREVIOUS run. Superseding happens here, not on the 202, so the old poll cannot
		 * report the old run's completion during the POST round-trip.
		 */
		shadingRecalcRequested(state) {
			state.data.isRecomputing = true;
			state.data.shadingError = null;
			// Predict the server's pick with the same rule the endpoint uses, so a label keyed on
			// runType is right from the click instead of showing the previous run's kind for the
			// length of the POST round-trip. The 202 overwrites it with the authoritative value.
			state.data.runType = state.data.dsmDirty ? 'dsm-edit' : 'facet-edit';
		},

		// Re-attach to a recompute already running server-side (e.g. after a deploy/reload
		// killed the poll loop). Versions are seeded from the fetched design; this just
		// flips the UI into "recomputing" and is the middleware's cue to restart the status poll.
		resumeShadingWatch(state) {
			state.data.isRecomputing = true;
			state.data.shadingError = null;
		},

		resetDsmShading() { return initialState; }, // call on design/uuid switch
	},

	// Free seeding from the design payload — zero coupling back into RoofDataSlice.
	extraReducers: (builder) => {
		const seed = (
			state: { data: DsmShadingState },
			snap?: ShadingStatusSnap,
		) => {
			if (!snap) return;
			const s = state.data;
			// don't clobber an in-flight recompute unless the incoming snapshot is itself in sync
			if (s.isRecomputing && !snap.inSync) return;
			s.dsmVersion = Math.max(s.dsmVersion, snap.dsmVersion);
			s.shadingVersion = Math.max(s.shadingVersion, snap.shadingVersion);
			s.shadingStatus = snap.shadingStatus;
			s.inSync = snap.inSync;
			s.dsmDirty = snap.dsmDirty;
			s.runType = snap.runType;
			if (snap.shadingStatus === SHADING_STATUS.COMPLETED && snap.inSync)
				s.isRecomputing = false;
		};
		// Both thunks resolve with a `roofData` (TransformedSolarData) → shadingAnalysis lives
		// there. inSync/runType are derived: the design payload carries the columns, not the
		// flags the status route computes.
		const pick = (
			payload: Pick<TransformedSolarData, 'roofData'>,
		): ShadingStatusSnap | undefined => {
			const analysis = payload?.roofData?.shadingAnalysis;
			if (!analysis) return undefined;
			return {
				dsmVersion: analysis.dsmVersion,
				shadingVersion: analysis.shadingVersion,
				shadingStatus: analysis.shadingStatus,
				inSync: analysis.dsmVersion === analysis.shadingVersion,
				dsmDirty: analysis.dsmDirty,
				runType: analysis.dsmDirty ? 'dsm-edit' : 'facet-edit',
			};
		};
		builder
			.addCase(changePanel.fulfilled, (state, action) =>
				seed(state, pick(action.payload)),
			)
			.addCase(fetchRoofData.fulfilled, (state, action) =>
				seed(state, pick(action.payload)),
			);
	},
});

export const {
	applyDsmEditResult,
	shadingRecalcAccepted,
	syncShadingState,
	syncCreateShadingState,
	shadingFailed,
	clearShadingError,
	shadingRecalcRequested,
	resumeShadingWatch,
	resetDsmShading,
	setFacetShadingState,
} = dsmShadingSlice.actions;

export default dsmShadingSlice.reducer;
